import { useEffect, useMemo, useRef } from "react";
import { DatePicker, Flex, Form } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
import { getActiveSetValue, getAllWidgets, getConditionalRevertValue, shouldApplyTodayDefault, shouldRevertConditionalValue } from "../../../utils/formsHelper";
import { DEFAULT_DATE_FORMAT, safeDayjs, toDatePickerValue } from "../../../utils/safeDate.js";
import dayjs from "dayjs"

const DateInput = (props) => {
    const {
        label,
        placeholder,
        tooltip,
        helpText,
        mandatory,
        readOnly,
        dateFormat,
        id,
        previewMode,
        setValueData,
        form,
        formValues,
        formElements,
        relativeDateEnabled,
        relativeDateReference,
        relativeDateDirection,
        relativeDateCountSource,
        relativeDateCount,
        relativeDateCountField,
        relativeDateUnit,
        isToday=false,
        requestDetail,
        defaultRequestDetail,
        disableAllFields
    } = props
    // "Today" is a default for a form being filled in, not a value recomputed every
    // time the request is opened: once it is submitted, the date it carries stays as
    // submitted. Wrapper gates its $TODAY set action on the same check.
    const todayDefaultApplies = isToday && shouldApplyTodayDefault({ requestDetail, defaultRequestDetail, fieldId: id, viewOnly: !!disableAllFields })
    const rules = mandatory ? [{ required: true }] : undefined;
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> }
        : undefined;
    const disabled = previewMode && readOnly ? true : false
    const activeSetValue = useMemo(
        () => getActiveSetValue(setValueData, true),
        [setValueData]
    )

    const currentDateFormat = dateFormat || DEFAULT_DATE_FORMAT

    // Never call dayjs() on a form value directly: a dayjs that lost its prototype
    // (cloned or rehydrated) still passes isDayjs, and dayjs() then calls .clone()
    // on it and throws. Everything in this file goes through here instead.
    // Partial formats (MMMM-YYYY, YYYY, MMM) also can't be parsed without knowing
    // the field's format, which toDatePickerValue tries before falling back to ISO.
    const toDayjsValue = (value, format = currentDateFormat) => toDatePickerValue(value, format)

    const lastAppliedConditionalRef = useRef(undefined)
    // The date the condition last wrote. The key above is the raw condition value,
    // which is not comparable with the dayjs the field actually holds.
    const appliedFormValueRef = useRef(undefined)

    useEffect(() => {
        const current = form?.getFieldValue(id)
        const currentDayjs = toDayjsValue(current)

        const applyConditional = (newValue, key) => {
            if (disabled) {
                if (!currentDayjs || !currentDayjs.isSame(newValue, "day")) {
                    form?.setFieldValue(id, newValue)
                }
            } else if (lastAppliedConditionalRef.current !== key) {
                form?.setFieldValue(id, newValue)
            }
            lastAppliedConditionalRef.current = key
            appliedFormValueRef.current = newValue
        }

        if (activeSetValue?.value === "$TODAY") {
            applyConditional(dayjs().startOf("day"), "$TODAY")
            return
        }
        if (activeSetValue?.value) {
            applyConditional(safeDayjs(activeSetValue.value, DEFAULT_DATE_FORMAT), activeSetValue.value)
            return
        }
        if (!isToday && shouldRevertConditionalValue(activeSetValue, { disabled, currentValue: currentDayjs, appliedValue: appliedFormValueRef.current })) {
            const fallback = getConditionalRevertValue(id, props.defaultFormValues)
            const nextValue = fallback != null ? toDayjsValue(fallback) : null
            if (current != null || nextValue != null) {
                form?.setFieldValue(id, nextValue)
            }
            lastAppliedConditionalRef.current = undefined
            appliedFormValueRef.current = undefined
            return
        }
        lastAppliedConditionalRef.current = undefined
        appliedFormValueRef.current = undefined
        if (todayDefaultApplies && previewMode && !formValues?.[id]) {
            form?.setFieldValue(id, dayjs().startOf("day"))
        }
    }, [activeSetValue, disabled, form, id, isToday, todayDefaultApplies, previewMode, formValues?.[id]])

    // The base field renders in its own format, so what sits in the store for it can
    // be a string this field's format would never parse ("26-08-2026" read as
    // YYYY-MM-DD). Parse it with the format the referenced field actually uses and
    // only then fall back to loose parsing.
    const referenceDateFormat = useMemo(() => {
        if (!relativeDateReference || !formElements) return null
        const referenced = getAllWidgets(formElements).find((widget) => widget.id === relativeDateReference)
        return referenced?.dateFormat || null
    }, [formElements, relativeDateReference])

    // A dynamic count reads its number from another field on the form, so it can
    // change while the form is being filled; a static one is fixed by the author.
    // The form store is read first: a condition writes its "set" value with
    // form.setFieldValue, and the store is where that value lands before the
    // watched values catch up.
    const getRelativeCount = () => {
        // `relativeDateCountSource` arrived after the static-only version of this
        // feature, so a configured count field is authoritative on its own - a widget
        // that kept a stale "static" source must not silently fall back to a count
        // that switching to dynamic already cleared.
        if (relativeDateCountField) {
            return form?.getFieldValue(relativeDateCountField) ?? formValues?.[relativeDateCountField]
        }
        if (relativeDateCountSource === "dynamic") return null
        return relativeDateCount
    }

    // A number field stores its value as a string, and can carry grouping commas.
    const toCount = (value) => {
        if (value === undefined || value === null || value === "") return null
        const numeric = Number(typeof value === "string" ? value.replace(/,/g, "").trim() : value)
        return Number.isFinite(numeric) ? numeric : null
    }

    const relativeBaseValue = relativeDateEnabled && relativeDateReference
        ? (form?.getFieldValue(relativeDateReference) ?? formValues?.[relativeDateReference])
        : undefined

    const getRelativeDateValue = () => {
        if (!relativeDateEnabled || !relativeDateReference) return null
        const delta = toCount(getRelativeCount())
        if (delta === null) return null
        if (!relativeBaseValue) return null
        const baseDate = toDayjsValue(relativeBaseValue, referenceDateFormat)
        if (!baseDate) return null
        const direction = relativeDateDirection || "after"
        const unit = relativeDateUnit || "day"
        return direction === "before" ? baseDate.subtract(delta, unit) : baseDate.add(delta, unit)
    }

    // Derived during render rather than inside the effect: the count can be written
    // into the store by a condition (form.setFieldValue), which the `formValues`
    // snapshot does not always carry in time to serve as the effect's trigger. The
    // computed target itself is the dependency, so any change to the base date, the
    // count, the direction or the unit re-applies it - and nothing else does.
    const relativeTarget = getRelativeDateValue()
    const relativeTargetKey = relativeTarget ? relativeTarget.toISOString() : null

    useEffect(() => {
        if (!relativeDateEnabled || !relativeDateReference) return
        // The value is derived, so it only lives as long as the rule can produce it:
        // no base date, or no count once a condition stops writing into the field the
        // count is read from, and the field clears with them instead of holding on to
        // the date the rule produced a moment ago.
        if (!relativeBaseValue || !relativeTargetKey) {
            if (form?.getFieldValue(id) != null) {
                form?.setFieldValue(id, null)
            }
            return
        }
        const computed = safeDayjs(relativeTargetKey)
        const currentDate = toDayjsValue(form?.getFieldValue(id))
        if (!currentDate || !currentDate.isSame(computed, "day")) {
            form?.setFieldValue(id, computed)
        }
    }, [
        relativeDateEnabled,
        relativeDateReference,
        relativeBaseValue,
        relativeTargetKey,
        // The field's own value is a dependency so that editing it re-runs this and
        // puts the rule's value straight back. A relative date is derived from the
        // base field and the count, so the rule owns it whether or not the picker
        // happens to be enabled - an editable one is not an invitation to hold a
        // value the rule disagrees with.
        formValues?.[id],
        form,
        id
    ])

    // A format without a day (MM-YYYY / MMM) needs the month panel, and one with
    // only a year needs the year panel - otherwise the day picker produces values
    // the format can't round-trip and antd rejects them as invalid dates.
    const picker = currentDateFormat === "YYYY"
        ? "year"
        : !currentDateFormat.includes("D")
            ? "month"
            : "date"

    return (
        <div className="form-element">
            <Flex vertical>
                <Form.Item
                    name={id}
                    label={label}
                    tooltip={help}
                    rules={rules}
                    getValueProps={(value) => ({ value: toDayjsValue(value) })}
                >
                    <DatePicker
                        picker={picker}
                        size="large"
                        placeholder={placeholder}
                        style={{ width: "100%" }}
                        format={currentDateFormat}
                        disabled={disabled}
                    />
                </Form.Item>
            </Flex>
        </div>
    )
}

export default DateInput
